import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import logger from '@/lib/logger';
import { Check, Spinner, Warning, Crown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      }
      if (data.status === 'expired') {
        setStatus('error');
        return;
      }
      // Keep polling
      if (attempts < 10) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setStatus('error');
      }
    } catch (err) {
      logger.error('Payment status poll error:', err);
      if (attempts < 10) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => { pollStatus(); }, [pollStatus]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="payment-success-page">
      <div className="text-center max-w-md">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="text-3xl font-black tracking-tighter mb-2">PROCESSING PAYMENT</h2>
            <p className="text-[#A1A1AA]">Verifying your payment with Stripe...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#34C759]/10 border border-[#34C759]/30 flex items-center justify-center mx-auto mb-6">
              <Crown weight="fill" className="w-10 h-10 text-[#FF3B30]" />
            </div>
            <h2 className="text-4xl font-black tracking-tighter mb-3">YOU'RE PRO!</h2>
            <p className="text-[#34C759] font-bold text-lg mb-2">Payment successful!</p>
            <p className="text-[#A1A1AA] mb-8">
              You now have full access to AI workout plans, nutrition recommendations, custom workouts, and more.
            </p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary px-8 py-3 text-lg" data-testid="go-to-dashboard">
              Go to Dashboard
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center mx-auto mb-6">
              <Warning weight="bold" className="w-10 h-10 text-[#FF3B30]" />
            </div>
            <h2 className="text-3xl font-black tracking-tighter mb-3">PAYMENT ISSUE</h2>
            <p className="text-[#A1A1AA] mb-8">
              We couldn't verify your payment. If you were charged, your Pro access will activate shortly.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate('/pricing')} className="btn-primary px-6 py-3">
                Try Again
              </button>
              <button onClick={() => navigate('/dashboard')} className="btn-secondary px-6 py-3">
                Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
